import { ISensitiveFieldInfo, OmitStrict } from '@sparelabs/core'
import { IPoint } from '@sparelabs/geography'
import { IListModelQueryParamValues, IModelJsonValues } from '@sparelabs/http-client-utils'
import { IRequestAccessibilityFeature } from './AccessibilityTypes'
import { CustomFieldFindOptionQuery } from './CustomFieldTypes'
import { CreatedInterface } from './EstimateTypes'
import { NotificationDeliveryMethod, RiderNotificationPreference } from './NotificationSettingTypes'
import { IRequestRiderType } from './RequestRiderTypes'
import { Language } from './SharedTypes'
import { IPartialUserDataPostBody, IPartialUserDataResponse, UserStatus } from './UserTypes'

export interface IOrganizationRiderResponse {
  metadata: Record<string, unknown>
  defaultRequestMetadata: Record<string, unknown>
  defaultPaymentMethodId: string | null
  defaultPaymentMethodType: string | null
  defaultRiders: IRequestRiderType[] | null
  defaultAccessibilityFeatures: IRequestAccessibilityFeature[] | null
  defaultNotes: string | null
  externalNumericId: number | null
  channelPreference: NotificationDeliveryMethod | null
  languagePreference: Language | null
  notificationPreference: RiderNotificationPreference[]
  status: UserStatus
  archivedInterface: CreatedInterface | null
  archivedTs: number | null
  isEmailVerified: boolean | null
  useEmailAuthentication: boolean
  isPhoneNumberVerified: boolean | null
  usePhoneNumberAuthentication: boolean
}

export interface IRiderMeResponse {
  id: string
  firstName: string | null
  lastName: string | null
  photoUrl: string | null
  phoneNumber: string | null
  isPhoneNumberVerified: boolean | null
  usePhoneNumberAuthentication: boolean
  email: string | null
  isEmailVerified: boolean | null
  useEmailAuthentication: boolean
  defaultPaymentMethodId: string | null
  defaultRiders: IRequestRiderType[] | null
  defaultAccessibilityFeatures: IRequestAccessibilityFeature[] | null
  defaultNotes: string | null
  defaultRequestMetadata: Record<string, unknown>
  externalNumericId: number | null
  channelPreference: NotificationDeliveryMethod | null
  languagePreference: Language | null
  notificationPreference: RiderNotificationPreference[]
  metadata: Record<string, unknown>
  status: UserStatus
}

export interface IRiderResponse extends IModelJsonValues, IPartialUserDataResponse, IOrganizationRiderResponse {
  lastSeenTs: number | null
  fraudLevel: RiderFraudLevel
}

export interface IRiderListQueryParams extends IListModelQueryParamValues {
  email?: string
  phoneNumber?: string
  search?: string
  groupId?: string
  fromLastSeenTs?: number
  toLastSeenTs?: number
  fraudLevel?: RiderFraudLevel
  customFields?: CustomFieldFindOptionQuery
  status?: UserStatus[]
  archivedInterface?: CreatedInterface
  fromArchivedTs?: number
  toArchivedTs?: number
  fromUpdatedAt?: number
  toUpdatedAt?: number
}

export interface IRiderPostBody
  extends IPartialUserDataPostBody,
    Partial<
      OmitStrict<
        IOrganizationRiderResponse,
        | 'defaultPaymentMethodId'
        | 'defaultPaymentMethodType'
        | 'archivedInterface'
        | 'archivedTs'
        | 'isEmailVerified'
        | 'isPhoneNumberVerified'
      >
    > {}

export interface IRiderPatchBody
  extends IPartialUserDataPostBody,
    Partial<
      OmitStrict<
        IOrganizationRiderResponse,
        | 'defaultPaymentMethodType'
        | 'archivedTs'
        | 'isEmailVerified'
        | 'isPhoneNumberVerified'
        | 'status'
        | 'archivedInterface'
      >
    > {}

export interface IRiderMePatchBody {
  firstName?: string | null
  lastName?: string | null
  photoUrl?: string | null
  phoneNumber?: string | null
  usePhoneNumberAuthentication?: boolean
  email?: string | null
  useEmailAuthentication?: boolean
  defaultPaymentMethodId?: string | null
  defaultRiders?: IRequestRiderType[] | null
  defaultAccessibilityFeatures?: IRequestAccessibilityFeature[] | null
  defaultNotes?: string | null
  defaultRequestMetadata?: Record<string, unknown> | null
  externalNumericId?: number | null
  channelPreference?: NotificationDeliveryMethod | null
  languagePreference?: Language | null
  notificationPreference?: RiderNotificationPreference[]
  metadata?: Record<string, unknown> | null
  /**
   * @deprecated Use a dedicated archive endpoint instead.
   */
  status?: UserStatus
  /**
   * @deprecated Use a dedicated archive endpoint instead.
   */
  archivedInterface?: CreatedInterface
}

export interface IRiderMeJoinGroupPostBody {
  secretCode: string
}

export enum ExternalPlatform {
  Routematch = 'Routematch',
  Trapeze = 'Trapeze',
}

export interface IRiderAddressSuggestion {
  address: string
  location: IPoint
}

export enum RiderFraudLevel {
  Low = 'low',
  Medium = 'medium',
  High = 'high',
}

export interface IRiderPhonePinPostBody {
  phonePin: string
}

export interface IRiderPhonePinResponse {
  isPhonePinSet: boolean
}

export interface IRiderSummaryQueryParams {
  fromTs?: number
  toTs?: number
}

export interface IRiderSummaryResponse {
  summary?: string[]
}

export interface IRiderExportStub {
  id: string
  firstName: string | null
  lastName: string | null
  email: string | null
  phoneNumber: string | null
  externalNumericId: number | null
}

export interface IRiderBulkDeletePostBody {
  ids: string[]
}

export interface IRiderMeConflictCheckPostBody {
  email?: string
  phoneNumber?: string
}

export interface IRiderMeConflictCheckResponse {
  hasConflict: boolean
}

// Not really a type but it's kind of nice to keep this here. (THIS IS A HACKY 1-DAY MASKING SOLUTION, NOT NOT ASSUME IT WORKS PROPERLY EVERYWHERE)
export const riderSensitiveFields: Array<
  ISensitiveFieldInfo<{ email?: string | null | undefined; metadata?: Record<string, unknown> }>
> = [{ field: 'email' }]
